import { AuthContext, IntlContext, ListContext } from "context";
import { useQuery } from "@tanstack/react-query";
import dataFetcher, { BookingInterface, BookingStatus } from "hooks/booking";
import { Category } from "types/category";
import { SUBCATEGORY_TO_CATEGORY, SubCategory } from "types/subCategory";
import { Role } from "types/user";
import moment from "moment";
import React, { useContext, useState } from "react";
import { useNavigate, useParams } from "react-router";
import Button from "ui/Button";
import { Loader } from "ui/Loader";
import { TextView } from "ui/View";
import RefuseServiceModal from "../List/RefuseBookingModal";
import ConfirmModal from "ui/Modal/ConfirmModal";
import { NavLink } from "react-router-dom";
import Label from "ui/Form/Element/Label";
import CalculOpperator from "helpers/CalculOpperator";
import { FontAwesomeIcon as Icon } from "@fortawesome/react-fontawesome";
import formatReferencesValues from "helpers/form/formatReferencesValues";
import Footer from "components/Layout/Main/Footer";
import { useReferences } from "hooks/reference";
import { ROUTES, buildRoute } from "routes";

const ViewBooking: React.FC = () => {
    const intl = useContext(IntlContext);
    const { account, currentRole } = useContext(AuthContext);
    const reference = useReferences();
    const { refreshData } = useContext(ListContext);
    const navigate = useNavigate();
    const { id } = useParams();
    const { isLoading, data } = useQuery({
        queryKey: ["booking", id],
        queryFn: () => dataFetcher.one(id as string),
        enabled: !!id,
    });
    const [isHiddenRefuseServiceModal, setIsHiddenRefuseServiceModal] = useState<boolean>(true);
    const [showCancelBookingModal, setShowCancelBookingModal] = useState<boolean>(false);
    const [selectedBookingId, setSelectedBookingId] = useState<string>();
    const [displayAddress] = useState<string>("");

    const formatDate = (date: string) => {
        const dateFormatted = moment(date);
        return dateFormatted.locale("fr").format("LL");
    };

    const handleOnClickBack = () => {
        if (account?.user.roles[0] === Role.PROVIDER) {
            navigate(ROUTES.SERVICE_HISTORY);
        } else {
            navigate(`${ROUTES.MY_RESERVATIONS}?filter_status=${data?.status ?? ""}`);
        }
    };

    const handleConfirmCancelModal = async () => {
        setShowCancelBookingModal(false);
        if (
            (data?.status === BookingStatus.PENDING || data?.status === BookingStatus.TO_VALIDATE) &&
            selectedBookingId
        ) {
            await dataFetcher.cancel(selectedBookingId);
            refreshData();
            handleOnClickBack();
        }
    };

    const renderFieldsByCategoryAndSubCategory = (data: BookingInterface) => {
        let items = [];

        switch (String(data.category)) {
            case Category.ASSISTANCE.toString():
                if ([SubCategory.TROUBLESHOOTING].includes(data.subcategory)) {
                    const deviceType = data.data?.device_type;
                    // TODO Utiliser le référentiel pour avoir la bonne valeur
                    items.push(
                        <div className="grid__col--6 grid__col--medium-6 grid__col--small-12" key={"device_type"}>
                            <TextView
                                value={formatReferencesValues(reference, "device", deviceType)}
                                label={intl.formatMessage("booking.details.label.device_type")}
                            />
                        </div>
                    );
                }
                break;
            case Category.HOUSEKEEPING.toString():
                items.push(
                    <React.Fragment>
                        {[SubCategory.CLEANING].includes(data.subcategory) && (
                            <div
                                className="grid__col--6 grid__col--medium-6 grid__col--small-12"
                                key={"bring_products"}
                            >
                                <TextView
                                    value={
                                        data.data?.bring_products === "1"
                                            ? intl.formatMessage("generic.label.yes")
                                            : intl.formatMessage("generic.label.no")
                                    }
                                    label={intl.formatMessage("booking.details.label.bring")}
                                />
                            </div>
                        )}
                    </React.Fragment>
                );
                break;
            case Category.CHILDCARE.toString():
                items.push(
                    <React.Fragment>
                        <div className="grid__col--6 grid__col--medium-6 grid__col--small-12" key={"child_count"}>
                            <TextView
                                value={data.data?.child_count}
                                label={intl.formatMessage("booking.details.label.child_count")}
                            />
                        </div>
                        <div className="grid__col--6 grid__col--medium-6 grid__col--small-12" key={"child_age"}>
                            <TextView
                                value={data.data?.child_age}
                                label={intl.formatMessage("booking.details.label.child_age")}
                            />
                        </div>
                    </React.Fragment>
                );
                break;
            case Category.GARDENING.toString():
                /*if ([SubCategory.PRUNING].includes(data.subcategory)) {
                    // à voir avec la lcient sinon supprimer
                    items.push(
                        <React.Fragment>
                            <div
                                className="grid__col--6 grid__col--medium-6 grid__col--small-12"
                                key={"watering_place"}
                            >
                                <TextView
                                    value={intl.formatMessage(
                                        "booking.details.label.watering_place." + data.data.watering_place
                                    )}
                                    label={intl.formatMessage("booking.details.label.watering_place")}
                                />
                            </div>
                        </React.Fragment>
                    );
                } else {*/
                    items.push(
                        <React.Fragment>
                            {/*<div
                                className="grid__col--6 grid__col--medium-6 grid__col--small-12"
                                key={"watering_place"}
                            >
                                <TextView
                                    value={intl.formatMessage(
                                        "booking.details.label.watering_place." + data.data.watering_place
                                    )}
                                    label={intl.formatMessage("booking.details.label.watering_place")}
                                />
                            </div>*/}
                            <div
                                className="grid__col--6 grid__col--medium-6 grid__col--small-12"
                                key={"bring_tools"}
                            >
                                <TextView
                                    value={
                                        data.data?.bring_tools === "1"
                                            ? intl.formatMessage("generic.label.yes")
                                            : intl.formatMessage("generic.label.no")
                                    }
                                    label={intl.formatMessage("booking.details.label.bring")}
                                />
                            </div>
                        </React.Fragment>
                    );
                //}
                break;
            case Category.PRIVATE_TUTORING.toString():
                if ([SubCategory.HOMEWORK].includes(data.subcategory)) {
                    items.push(
                        <React.Fragment>
                            <div
                                className="grid__col--6 grid__col--medium-6 grid__col--small-12"
                                key={"scholar_subject"}
                            >
                                <TextView
                                    value={formatReferencesValues(reference, "scholar_subject", data.data?.scholar_subjects)}
                                    label={intl.formatMessage("booking.details.label.subject")}
                                />
                            </div>
                            <div
                                className="grid__col--6 grid__col--medium-6 grid__col--small-12"
                                key={"academic_level"}
                            >
                                <TextView
                                    value={formatReferencesValues(reference, "scholar_level", data.data?.academic_level)}
                                    label={intl.formatMessage("booking.details.label.academic_level")}
                                />
                            </div>
                        </React.Fragment>
                    );
                }
                if ([SubCategory.FOREIGN_LANGUAGES].includes(data.subcategory)) {
                    items.push(
                        <React.Fragment>
                            <div
                                className="grid__col--6 grid__col--medium-6 grid__col--small-12"
                                key={"foreign_languages"}
                            >
                                <TextView
                                    value={formatReferencesValues(reference, "foreign_languages", data.data?.foreign_languages)}
                                    label={intl.formatMessage("booking.details.label.foreign_languages")}
                                />
                            </div>
                        </React.Fragment>
                    );
                }
                if ([SubCategory.OTHER_LESSON].includes(data.subcategory)) {
                    items.push(
                        <React.Fragment>
                            <div
                                className="grid__col--6 grid__col--medium-6 grid__col--small-12"
                                key={"activity"}
                            >
                                <TextView
                                    value={formatReferencesValues(reference, "activity", data.data?.activities)}
                                    label={intl.formatMessage("booking.details.label.activity")}
                                />
                            </div>
                        </React.Fragment>
                    );
                }
                break;
        }

        return items;
    };

    if (isLoading || !data) {
        return <Loader />;
    }

    const totalPrice = parseFloat(data.price) * data.nb_hours;
    const opperator = new CalculOpperator(data);
    const bookingDateTime = moment(moment(data.date).format("YYYY-MM-DD") + ' ' + data.start_time);
    let [managementFeesValue] = opperator.computeTTC(totalPrice);
    let supportedCompany = data.supported_company ? parseFloat(data.supported_company) : 0;
    let role = currentRole == Role.CLIENT ? Role.CLIENT : Role.PROVIDER;

    return (
        <React.Fragment>
            <div className="dashboard-page">
                <div className="block-header">
                    <h1 className="block-header__title">Détails de la <span>réservation</span></h1>
                </div>
                <div className="container">
                    <div className="our_services_view_container top-shadow">
                        <Button className="btn btn--secondary btn--square btn--return" onClick={() => handleOnClickBack}>
                            <span className="icon icon-arrow-left"></span>
                            {intl.formatMessage("generic.action.back")}
                        </Button>
                        <NavLink
                            to={buildRoute.myReservationMessages(data.service_id, data.client_id)}
                            className="btn btn--primary btn__outline--square booking_details_title_button"
                            style={{position:"absolute", right:"16px", top:"16px"}}
                        >
                            {intl.formatMessage("booking.details.show.conversation")}
                        </NavLink>
                        <div className="booking_details_title margin-top--3">
                            <h3>
                                <span>
                                    Réservation du {formatDate(data.date)} :&nbsp;
                                </span>
                                <span className={"text-color--" + data.category.toString().toLocaleLowerCase()}>
                                    {intl.formatMessage("category." + data.category.toString().toLocaleLowerCase())}
                                </span>
                                {data.subcategory && (
                                    <span className={"text-color--" + data.category.toString().toLocaleLowerCase()}>
                                        &nbsp;- {intl.formatMessage(
                                            "subcategory." +
                                            SUBCATEGORY_TO_CATEGORY[data.subcategory].toLocaleLowerCase() +
                                            "." +
                                            data.subcategory.toLocaleLowerCase() +
                                            ".label"
                                        )}
                                    </span>
                                )}
                            </h3>
                        </div>

                        <p className="margin-top--3 text-underline">
                            {intl.formatMessage("booking.details.label.general_informations")}
                        </p>

                        <div className="grid--12 grid--has-gutter-3x margin-top--3">
                            <div className="grid__col--6 grid__col--medium-6 grid__col--small-12">
                                <TextView
                                    value={
                                        account?.user.roles[0] === Role.PROVIDER
                                            ? data.client.last_name + " " + data.client.first_name
                                            : data.provider.last_name + " " + data.provider.first_name
                                    }
                                    label={
                                        account?.user.roles[0] === Role.PROVIDER
                                            ? intl.formatMessage("booking.details.label.lastname_firstname_client")
                                            : intl.formatMessage("booking.details.label.lastname_firstname_presta")
                                    }
                                />
                            </div>
                            <div className="grid__col--6 grid__col--medium-6 grid__col--small-12">
                                {/* @TODO  */}
                                <TextView
                                    value={data.code}
                                    label={intl.formatMessage("booking.details.label.number_of_booking")}
                                />
                            </div>
                        </div>

                        <div className="grid--12 grid--has-gutter-3x margin-top--3">
                            <div className="grid__col--6 grid__col--medium-6 grid__col--small-12">
                                <TextView
                                    value={intl.formatMessage("booking.details.label.hours.value", {
                                        hours: data.start_time.split(":")[0],
                                    })}
                                    label={intl.formatMessage("booking.details.label.hours")}
                                />
                            </div>
                            <div className="grid__col--6 grid__col--medium-6 grid__col--small-12">
                                <TextView
                                    value={intl.formatMessage("booking.details.label.duration.value", {
                                        duration: data.nb_hours,
                                    })}
                                    label={intl.formatMessage("booking.details.label.duration")}
                                />
                            </div>
                        </div>

                        <div className="grid--12 grid--has-gutter-3x margin-top--3">
                            <div className="grid__col--12">
                                <TextView
                                    value={displayAddress}
                                    label={intl.formatMessage("booking.details.label.address")}
                                />
                            </div>
                        </div>

                        <div className="grid--12 grid--has-gutter-3x margin-top--3">
                            <div className="grid__col--12">
                                <TextView
                                    value={data.booking_comment}
                                    label={intl.formatMessage("booking.details.label.description")}
                                />
                            </div>
                        </div>

                        <p className="margin-top--5 text-underline">
                            {intl.formatMessage("booking.details.label.recap")}
                        </p>

                        {data.data && Object.keys(data.data).length > 0 && (
                            <div className="grid--12 grid--has-gutter-3x margin-top--3">
                                {renderFieldsByCategoryAndSubCategory(data).map((element) => element)}
                            </div>
                        )}

                        <div className="grid--12 grid--has-gutter-3x margin-top--3">
                            <div className="grid__col--6 grid__col--medium-6 grid__col--small-12">
                                <TextView
                                    value={data.activate_cesu_plus || data.cesu_plus_status ? "CESU+" : intl.formatMessage("booking.details.label.mode_payment.credit_card")}
                                    label={intl.formatMessage("booking.details.label.mode_payment")}
                                />
                            </div>
                            
                        </div>

                        <p className="margin-top--5 text-underline">
                            {intl.formatMessage("booking.details.label.details_invoice")}
                        </p>
                        <div className="grid--12 grid--has-gutter-3x margin-top--3">
                            
                            <div className="grid__col--6 grid__col--medium-6 grid__col--small-12">
                                <TextView
                                    value={totalPrice}
                                    label={intl.formatMessage("booking.details.label.details_invoice.rate_hour")}
                                    symbol=" €"
                                />
                            </div>
                            {account?.user.roles[0] === Role.CLIENT && (
                            <div className="grid__col--6 grid__col--medium-6 grid__col--small-12">
                                <TextView
                                    value={managementFeesValue}
                                    label={intl.formatMessage("booking.details.label.details_invoice.management_fees")}
                                    symbol=" €"
                                />
                            </div>
                            )}
                        </div>

                        {account?.user.roles[0] === Role.CLIENT && (
                            <div className="grid--12 grid--has-gutter-3x margin-top--3">
                                
                                {account?.resources?.client && account?.resources?.client[0].company_id &&
                                <div className="grid__col--6 grid__col--medium-6 grid__col--small-12">
                                    <TextView
                                        value={supportedCompany}
                                        label={intl.formatMessage(
                                            "booking.details.label.details_invoice.supported_company"
                                        )}
                                        symbol=" €"
                                    />
                                </div>
                                }
                                <div className="grid__col--6 grid__col--medium-6 grid__col--small-12">
                                    <TextView
                                        value={data.amount}
                                        label={intl.formatMessage("booking.details.label.details_invoice.total_amount")}
                                        symbol=" €"
                                    />
                                </div>
                                {data.employer_contributions && data.employee_contributions_ef && (
                                    <div className="grid__col--6 grid__col--medium-6 grid__col--small-12">
                                        <div className="grid--12 grid--has-gutter-3x margin-top--3">
                                            <div className="grid__col--6 grid__col--small-12">
                                                <Label className="field__label text-color--primary" style={{fontSize:"18px"}}>Détail URSSAF</Label>
                                                <p className="comment">
                                                    Cotisations patronales : {data.employer_contributions} €<br/>
                                                    Cotisations salariales : {data.employee_contributions_ef} €<br/>
                                                    Total cotisations sociales : <strong>{parseFloat(data.employer_contributions) + parseFloat(data.employee_contributions_ef)} €</strong>
                                                </p>
                                                <p className="comment margin-top--2">
                                                    Total provisoire prestation :
                                                    <span className="bold">&nbsp;
                                                        {(totalPrice + parseFloat(data.employer_contributions) + parseFloat(data.employee_contributions_ef) + managementFeesValue).toFixed(2)} €
                                                    </span>
                                                    <span className="btn-tooltip" style={{ marginLeft: "8px" }}>
                                                        <span className="icon icon-info" style={{fontSize:"14px"}}></span>

                                                        <span className="btn-tooltip__content">
                                                            Total "salaire + cotisations sociales + commission"
                                                        </span>
                                                    </span>
                                                </p>
                                                <p className="comment margin-top--2">
                                                    <p className="comment">
                                                        Estimation du crédit d'impôt :&nbsp;
                                                        <span className="bold">
                                                            {((totalPrice + parseFloat(data.employer_contributions) + parseFloat(data.employee_contributions_ef)) / 2).toFixed(2)} €
                                                        </span>
                                                        <span className="btn-tooltip" style={{ marginLeft: "8px" }}>
                                                            <span className="icon icon-info" style={{fontSize:"14px"}}></span>

                                                            <span className="btn-tooltip__content">
                                                                50% du total "salaire + cotisations sociales"
                                                            </span>
                                                        </span>
                                                    </p>
                                                </p>
                                            </div>
                                            <div className="grid__col--6 grid__col--small-12">
                                                <Label className="field__label text-color--primary" style={{fontSize:"18px"}}>En résumé :</Label>
                                                <div>
                                                    <ul className="text-color--success-dark" style={{ listStyleType: "none", fontSize: "14px", padding: "0", margin: "0"}}>
                                                        <li>
                                                            <Icon icon={{ prefix: "fas", iconName: "check" }} />&nbsp;
                                                            Vous payez&nbsp;
                                                            {!data.activate_cesu_plus && !data.cesu_plus_status ? (
                                                                <strong>{data.amount} €</strong>
                                                            ) : (
                                                                <strong>{managementFeesValue} €</strong>
                                                            )}
                                                            &nbsp;maintenant
                                                        </li>
                                                        <li style={{ margin: "8px 0" }}>
                                                            <Icon icon={{ prefix: "fas", iconName: "check" }} />&nbsp;&nbsp;
                                                            L'URSSAF prélève automatiquement vos cotisations sociales : <strong>{(parseFloat(data.employer_contributions) + parseFloat(data.employee_contributions_ef)).toFixed(2)} €</strong>
                                                        </li>
                                                        {!!(data.activate_cesu_plus || data.cesu_plus_status) && (
                                                            <li style={{ margin: "8px 0" }}>
                                                                <Icon icon={{ prefix: "fas", iconName: "check" }} />&nbsp;&nbsp;
                                                                L'URSSAF prélève automatiquement le salaire du prestataire : <strong>{totalPrice} €</strong>
                                                            </li>
                                                        )}
                                                        <li style={{ margin: "8px 0" }}>
                                                            <Icon icon={{ prefix: "fas", iconName: "check" }} />&nbsp;&nbsp;
                                                            Vous bénéficiez d'un crédit d'impôt de <strong>{((totalPrice + parseFloat(data.employer_contributions) + parseFloat(data.employee_contributions_ef)) / 2).toFixed(2)} €</strong>&nbsp;
                                                            {data.aici_status || data.activate_cesu_plus ? (
                                                                <u>immédiatement</u>
                                                            ) : (
                                                                <u>l'année suivante</u>
                                                            )}
                                                        </li>
                                                        <li className="text-color--primary">
                                                            Coût net final : <strong>{(totalPrice + parseFloat(data.employer_contributions) + parseFloat(data.employee_contributions_ef) + managementFeesValue - (totalPrice + parseFloat(data.employer_contributions) + parseFloat(data.employee_contributions_ef)) / 2).toFixed(2)} €</strong>
                                                        </li>
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {(data?.status === BookingStatus.PENDING) && (
                            <div className="booking_details_actions">
                                <Button
                                    onClick={() => {
                                        role === Role.CLIENT ? setShowCancelBookingModal(true) : setIsHiddenRefuseServiceModal(false);
                                        setSelectedBookingId(id);
                                    }}
                                    className={role === Role.PROVIDER && bookingDateTime.diff(moment(), 'hours') === 0 ? "" : "btn btn__outline--cancel btn__outline--square"}
                                    disabled={role === Role.PROVIDER && bookingDateTime.diff(moment(), 'hours') === 0}
                                >
                                    {role === Role.PROVIDER
                                        ? (bookingDateTime.diff(moment(), 'hours') === 0 ? intl.formatMessage("booking.list.links.label.provider_cancel.booking.disabled") : intl.formatMessage("booking.list.links.label.provider_cancel.booking"))
                                        : intl.formatMessage("booking.list.links.label.cancel.booking")}
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {id === selectedBookingId && (
                <React.Fragment>
                    <RefuseServiceModal
                        isHiddenRefuseServiceModal={isHiddenRefuseServiceModal}
                        setIsHiddenRefuseServiceModal={setIsHiddenRefuseServiceModal}
                        booking={data}
                        dateFormatter={formatDate}
                        role={role}
                        startTimeSplit={data.start_time.split(":")[0]}
                    />
                    <ConfirmModal
                        show={showCancelBookingModal}
                        onClose={() => setShowCancelBookingModal(false)}
                        onConfirm={() => handleConfirmCancelModal()}
                        title={intl.formatMessage("booking.list.modal.cancel.booking.title")}
                        message={bookingDateTime.diff(moment(), 'hours') === 0 ? intl.formatMessage("booking.list.modal.cancel.booking.message") + " Attention, vous ne pourrez pas être remboursé car la prestation a lieu dans moins d'1h !" : intl.formatMessage("booking.list.modal.cancel.booking.message")}
                        confirmButton={intl.formatMessage("booking.list.links.label.cancel.booking")}
                        cancelClassName="btn btn--cancel"
                    />
                </React.Fragment>
            )}
            <Footer />
        </React.Fragment>
    );
};

export default ViewBooking;
